import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Users, CheckCircle, Send, Calendar, UserPlus, Loader2 } from 'lucide-react';
import { CJMSClient } from '../../api/cjmsApi';
import { feedbackBus } from '../../services/feedbackBus';

interface AdvisoryBoardProps {
    client: CJMSClient;
}

const INVITE_TEMPLATES = [
    {
        id: 'board',
        label: 'Advisory Board Invite',
        subject: 'Join Our Advisory Board',
        message: `Dear {{name}},\n\nWe are building an advisory board of our most valued partners, and we would love for you to be part of it. Your insights would help shape the future of our platform.\n\nWould you be open to a conversation about this?`,
    },
    {
        id: 'council',
        label: 'Customer Council Invite',
        subject: 'Customer Council Invitation',
        message: `Hi {{name}},\n\nWe are launching a customer council to get regular feedback from businesses like yours. As a valued partner, we would like to invite you to join.\n\nLet me know if you are interested!`,
    },
];

export const AdvisoryBoard: React.FC<AdvisoryBoardProps> = ({ client }) => {
    const isEligible = client.currentStage === 'Advocacy';
    const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
    const [customMessage, setCustomMessage] = useState('');
    const [status, setStatus] = useState<'idle' | 'sending' | 'sent'>('idle');

    if (!isEligible) return null;

    const handleSelectTemplate = (id: string) => {
        setSelectedTemplate(id);
        const template = INVITE_TEMPLATES.find(t => t.id === id);
        if (template) {
            setCustomMessage(template.message.replace(/\{\{name\}\}/g, client.name || client.email));
        }
    };

    const handleSend = async () => {
        setStatus('sending');
        await new Promise(r => setTimeout(r, 800));
        feedbackBus.emit('activity_completed', {
            activityLabel: `Advisory board invite sent to ${client.email}`,
            clientEmail: client.email,
        });
        setStatus('sent');
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden"
        >
            <div className="flex items-center gap-2 p-4 border-b border-slate-100">
                <Users className="w-4 h-4 text-indigo-600" />
                <h3 className="text-sm font-bold text-slate-800">Advisory Board Inviter</h3>
            </div>
            <div className="p-4 space-y-4">
                <div className="flex items-center gap-2 p-2 bg-indigo-50 rounded-lg border border-indigo-100">
                    <UserPlus className="w-3 h-3 text-indigo-600" />
                    <p className="text-[10px] text-indigo-700">Invite this client to join your advisory board or customer council</p>
                </div>
                <div className="space-y-2">
                    {INVITE_TEMPLATES.map(template => (
                        <button
                            key={template.id}
                            onClick={() => handleSelectTemplate(template.id)}
                            className={`w-full p-3 rounded-lg border text-left transition-all ${
                                selectedTemplate === template.id
                                    ? 'border-indigo-400 bg-indigo-50'
                                    : 'border-slate-100 bg-white hover:border-indigo-200'
                            }`}
                        >
                            <div className="flex items-center justify-between">
                                <span className="text-xs font-semibold text-slate-700">{template.label}</span>
                                {selectedTemplate === template.id && <CheckCircle className="w-3 h-3 text-indigo-500" />}
                            </div>
                            <p className="text-[10px] text-slate-500 mt-1">{template.subject}</p>
                        </button>
                    ))}
                </div>
                {selectedTemplate && (
                    <div className="space-y-3">
                        <div>
                            <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Message Preview</label>
                            <textarea
                                value={customMessage}
                                onChange={e => setCustomMessage(e.target.value)}
                                className="w-full mt-1 px-3 py-2 text-xs border border-slate-200 rounded-lg resize-none h-28 focus:ring-1 focus:ring-indigo-400 outline-none"
                            />
                        </div>
                        <div className="flex justify-between items-center">
                            <div className="flex items-center gap-1 text-[10px] text-slate-400">
                                <Calendar className="w-3 h-3" />
                                Will suggest a 30-min intro call
                            </div>
                            <button
                                onClick={handleSend}
                                disabled={status === 'sending'}
                                className="px-4 py-1.5 text-[11px] font-medium bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors disabled:opacity-50 flex items-center gap-1"
                            >
                                {status === 'sending' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                                Send Invite
                            </button>
                        </div>
                    </div>
                )}
                {status === 'sent' && (
                    <div className="flex items-center gap-2 p-2 bg-green-50 rounded-lg border border-green-100">
                        <CheckCircle className="w-3 h-3 text-green-500" />
                        <span className="text-[10px] text-green-700 font-medium">Invitation sent! Follow up in 3 days.</span>
                    </div>
                )}
            </div>
        </motion.div>
    );
};
